/*
 * Above 9 · Tranquitone · "Add to your set" rows in the cart drawer and on the cart page (original work, theme v0.1,
 * 2026-10-04; launch/ADDONS_PLAYBOOK.md v0.1, 5.4 item 5; v0.2.1 rows, DECISIONS.md row 32).
 *
 * Each row's Add button posts its one item (quantity 1, hidden property _a9_source = cart) to /cart/add.js, wrapped in
 * Horizon's own cart event so the drawer and the cart page re-render from the response: the added item's row then
 * goes (it is in the cart), and the next qualifying item may take its place. The listener sits on the rows' wrapper
 * (event delegation), so it keeps working when Horizon's morph swaps the rows. One request at a time; the tapped row
 * shows a loading state (aria-busy) until the cart answers. Without JavaScript a form posts to /cart/add as a plain
 * form. Errors show Shopify's own message under the rows. Nothing is added without the tap.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
  return response.json();
}

/**
 * @param {HTMLElement} source
 * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
 */
async function addLines(source, items) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent(
      /** @type {any} */ ({
        action: 'add',
        context: 'cart',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    )
  );

  let result;
  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSectionIds() }),
    });
    result = await response.json();
  } catch (error) {
    deferred.reject(error);
    throw error;
  }

  const failed = Boolean(result?.status) && result?.status !== 200;
  let cart = null;
  try {
    cart = await readCart();
  } catch (error) {
    deferred.reject(error);
  }
  if (cart) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: failed,
        items: cart.items,
        source: 'a9-cart-addon',
        sourceId: 'a9-cart-addon',
        itemCount: items.length,
        sections: failed ? undefined : result.sections,
      },
    });
  }
  if (failed) {
    source.dispatchEvent(
      new CartErrorEvent({
        error: result.message || 'Add to cart failed',
        code: 'INVALID',
        detail: { description: result.description, errors: result.errors },
      })
    );
    const reason = result.description || result.message || '';
    const failure = /** @type {Error & {shopifyMessage?: string}} */ (new Error(reason || 'Add to cart failed'));
    if (reason) failure.shopifyMessage = reason;
    throw failure;
  }
  return cart;
}

/**
 * The line shown under the item: Shopify's own reason when it sent one (a quantity limit, sold out), otherwise the
 * copy's fallback line from Theme settings (ADD-ERROR, v0.2) when set, otherwise the error's own text.
 * @param {unknown} error
 * @param {string | undefined} fallback
 */
function errorLine(error, fallback) {
  const shopify = error instanceof Error ? /** @type {Error & {shopifyMessage?: string}} */ (error).shopifyMessage : '';
  if (shopify) return shopify;
  if (fallback) return fallback;
  return error instanceof Error ? error.message : String(error);
}

class A9CartAddon extends HTMLElement {
  connectedCallback() {
    this.busy = false;
    this.onSubmit = (/** @type {SubmitEvent} */ event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[data-a9-cart-addon-form]')) return;
      this.handle(event, form);
    };
    this.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.onSubmit);
  }

  /**
   * @param {SubmitEvent} event
   * @param {HTMLFormElement} form
   */
  async handle(event, form) {
    event.preventDefault();
    if (this.busy) return;
    const data = new FormData(form);
    const id = Number(data.get('id'));
    if (!id) return;
    const button = form.querySelector('button[type="submit"]');
    const row = form.closest('.a9-cart-addon__row');
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-cart-addon-error]'));
    this.busy = true;
    button?.setAttribute('aria-busy', 'true');
    row?.classList.add('is-loading');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    try {
      await addLines(this, [{ id, quantity: 1, properties: { _a9_source: 'cart' } }]);
    } catch (failure) {
      if (error) {
        error.textContent = errorLine(failure, this.dataset.errorFallback);
        error.hidden = false;
      }
    } finally {
      this.busy = false;
      button?.removeAttribute('aria-busy');
      row?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('a9-cart-addon')) customElements.define('a9-cart-addon', A9CartAddon);
